import {formatCurrency} from './Currency.js'

/**
 * The lens credit a frame at `price` earns under the running lens credit event
 * (settings.promotions.lensCreditEvent: active, groups {key: [brand ids]},
 * tiers {key: [{min, credit}]}). 0 when the event is off, the brand is outside
 * it, or the price sits below the first tier. Search hits carry no server-side
 * credit, so listings derive it here from the price they display.
 */
export const lensCreditForPrice = ({brandId, price, lensCreditEvent}) => {
    if (!lensCreditEvent?.active) {
        return 0
    }

    const amount = Number(price)
    const id = Number(brandId)

    if (!Number.isFinite(amount) || amount <= 0 || !Number.isFinite(id)) {
        return 0
    }

    const groups = lensCreditEvent.groups || {}
    const groupKey = Object.keys(groups).find((key) => (groups[key] || []).some((brand) => Number(brand) === id))

    if (!groupKey) {
        return 0
    }

    const tiers = (lensCreditEvent.tiers?.[groupKey] || [])
        .map((tier) => ({min: Number(tier?.min), credit: Number(tier?.credit)}))
        .filter((tier) => Number.isFinite(tier.min) && Number.isFinite(tier.credit) && tier.credit > 0)
        .sort((a, b) => b.min - a.min)

    const match = tiers.find((tier) => amount >= tier.min)

    return match ? match.credit : 0
}

// Product-tag copy for a positive credit, e.g. "$250 Lens Credit"
export const lensCreditBadge = (credit) => {
    const amount = Number(credit)

    return Number.isFinite(amount) && amount > 0
        ? `${formatCurrency(amount)} Lens Credit`
        : null
}

// Whole dollars without cents, otherwise two decimals: "$1,499", "$99.50"
export const formatLensCreditDollars = (amount) => {
    const value = Number(amount)

    return `$${value.toLocaleString('en-US', {
        minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
        maximumFractionDigits: 2,
    })}`
}

const groupTiers = (lensCreditEvent, groupKey) => (lensCreditEvent?.tiers?.[groupKey] || [])
    .map((tier) => ({min: Number(tier?.min), credit: Number(tier?.credit)}))
    .filter((tier) => Number.isFinite(tier.min) && Number.isFinite(tier.credit) && tier.credit > 0)

// The biggest credit a group offers, for "Up to $750" headlines; 0 when it has none
export const maxLensCredit = (lensCreditEvent, groupKey) => {
    const credits = groupTiers(lensCreditEvent, groupKey).map((tier) => tier.credit)

    return credits.length ? Math.max(...credits) : 0
}

// Tier-table rows, cheapest first, e.g. {credit: '$250 Lens Credit', range: 'Frames $500 - $1,499'}
export const lensCreditTierRows = (lensCreditEvent, groupKey) => {
    const tiers = groupTiers(lensCreditEvent, groupKey).sort((a, b) => a.min - b.min)

    return tiers.map((tier, index) => {
        const next = tiers[index + 1]

        return {
            credit: `${formatLensCreditDollars(tier.credit)} Lens Credit`,
            range: next
                ? `Frames ${formatLensCreditDollars(tier.min)} - ${formatLensCreditDollars(next.min - 1)}`
                : `Frames ${formatLensCreditDollars(tier.min)}+`,
        }
    })
}
